import { isBlank } from '@loadsmart/utils-string';

import to from '../utils/await-to';
import logger from '../utils/logger';
import Memorycache from '../utils/memory-cache';
import toArray from '../utils/to-array';
import locationAdapter, { getFormattedLocation } from './location-adapter';

import { DATASOURCE_TYPE } from './location-select.constants';
import type {
	FetchLocationFields,
	FetchLocationOptions,
	FetchLocationType,
	Location,
	DataSource,
	LocationAdapter,
	CollectionLocationType,
	GeocodedLocation,
	Prediction,
	LocationResult,
	CompassConfig,
	LocationSelectProps,
} from './location-select.types';
import { DEFAULT_PARSERS } from './parsers';
import { CompassService } from './compass-service';
import { parseCompassGeocodeLocationToGeocodedLocation } from './compass-utils';
import { CompassGeocodeLocation } from './compass-api';

const cache = Memorycache<GeocodedLocation>();

let GoogleMapsMapsInstance: typeof google.maps | null;
let AutoCompleteServiceInstance: null | google.maps.places.AutocompleteService;
let PlacesServiceInstance: null | google.maps.places.PlacesService;
let GeocoderInstance: google.maps.Geocoder | null;

export function resetInstances(): void {
	GoogleMapsMapsInstance = null;
	AutoCompleteServiceInstance = null;
	PlacesServiceInstance = null;
	GeocoderInstance = null;

	cache.clear();
}

export function getGoogleMaps(): typeof google.maps | null {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- window.google.maps is not always truthy because it is possible to not have been loaded yet
	if (!GoogleMapsMapsInstance && window.google.maps) {
		GoogleMapsMapsInstance = window.google.maps;
	}

	return GoogleMapsMapsInstance;
}

export function getAutoCompleteService(): google.maps.places.AutocompleteService | null {
	if (AutoCompleteServiceInstance) {
		return AutoCompleteServiceInstance;
	}

	const googleMapsInstance = getGoogleMaps();

	if (!googleMapsInstance) {
		return null;
	}

	AutoCompleteServiceInstance =
		new googleMapsInstance.places.AutocompleteService();
	return AutoCompleteServiceInstance;
}

export function getPlacesService(): google.maps.places.PlacesService | null {
	if (PlacesServiceInstance) {
		return PlacesServiceInstance;
	}

	const googleMapsInstance = getGoogleMaps();

	if (!googleMapsInstance) {
		return null;
	}

	PlacesServiceInstance = new googleMapsInstance.places.PlacesService(
		document.createElement('div'),
	);

	return PlacesServiceInstance;
}

/**
 * Based on https://github.com/ubilabs/react-geosuggest/blob/660b66557f4f762986d13180f59e60031223fa07/src/Geosuggest.tsx#L340
 */
export function getGeocoder(): google.maps.Geocoder | null {
	if (GeocoderInstance) {
		return GeocoderInstance;
	}

	const googleMapsInstance = getGoogleMaps();

	if (!googleMapsInstance) {
		return null;
	}

	GeocoderInstance = new googleMapsInstance.Geocoder();

	return GeocoderInstance;
}

function parseResult(result: LocationResult): GeocodedLocation {
	const place_id = result.place_id;

	const parsed = DEFAULT_PARSERS.reduce(function runParser(obj, parser) {
		return {
			...obj,
			...parser(result),
		};
	}, {});

	return {
		...parsed,
		place_id,
	};
}

function applyCustomParser(
	query: string,
	geocodedLocation: GeocodedLocation,
	options?: FetchLocationOptions,
) {
	if (!options?.parser) {
		return geocodedLocation;
	}

	return {
		...options.parser(query, geocodedLocation),
		place_id: geocodedLocation.place_id,
	};
}

const SUPPORTED_LOCATION_TYPES = new Map<
	CollectionLocationType,
	FetchLocationType
>([
	['geocode', 'geocode'],
	['address', 'address'],
	['establishment', 'establishment'],
	['regions', '(regions)'],
	['cities', '(cities)'],
]);

/**
 * In order to have multiple location types, we need to make sure to pass only the
 * combined values in the following list:
 * https://developers.google.com/maps/documentation/javascript/supported_types#table2
 */
const UNMIXABLE_LOCATION_TYPES: string[] = [
	SUPPORTED_LOCATION_TYPES.get('address')!,
	SUPPORTED_LOCATION_TYPES.get('regions')!,
	SUPPORTED_LOCATION_TYPES.get('cities')!,
];

function getLocationType(type: CollectionLocationType): FetchLocationType {
	return SUPPORTED_LOCATION_TYPES.get(type)!;
}

function getTypes(options?: FetchLocationOptions): FetchLocationType[] {
	const types = toArray(options?.types)
		.map((type) => getLocationType(type))
		.filter(Boolean);

	if (types.length === 0) {
		return [SUPPORTED_LOCATION_TYPES.get('cities')!];
	}

	if (types.length === 1) {
		return types;
	}

	/**
	 * Check for the unmixable location types constraint.
	 * https://developers.google.com/maps/documentation/javascript/place-autocomplete#constrain-place-types
	 */
	if (types.some((type) => UNMIXABLE_LOCATION_TYPES.includes(type))) {
		logger.info(
			'Only the first location type will be considered. For more information, check the LocationSelect docs',
		);

		return types.slice(0, 1);
	}

	return types;
}

function getFields(options?: FetchLocationOptions): FetchLocationFields[] {
	const fields = options?.fields ?? null;

	if (!fields) {
		return ['address_components', 'place_id'];
	}

	return toArray(fields);
}

function getRestrictions(
	options?: FetchLocationOptions,
): google.maps.places.ComponentRestrictions {
	const restrictions = options?.restrictions ?? null;

	if (!restrictions) {
		return { country: 'us' };
	}

	return restrictions;
}

function getTags(suggest: google.maps.places.AutocompletePrediction): string[] {
	const tags: string[] = [];

	if (suggest.types.includes('postal_code_prefix')) {
		tags.push('is_zip3');
	}

	return tags;
}

function getAdapter(options?: FetchLocationOptions): LocationAdapter {
	return options?.adapter ?? locationAdapter;
}

function getFilteredPredictions(
	predictions: Prediction[],
	options: FetchLocationOptions | undefined,
): Prediction[] {
	const filters = toArray(options?.filters);
	return predictions.filter((prediction) => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- we need to understand why it is failing
		return filters.every((filter) => filter(prediction));
	});
}

async function getPlacePredictions(
	query: string,
	options?: FetchLocationOptions,
): Promise<Location[]> {
	const autocompleteService = getAutoCompleteService();
	if (!autocompleteService) {
		return [];
	}

	const predictionOpts = {
		input: query,
		types: getTypes(options),
		componentRestrictions: getRestrictions(options),
	};

	logger.info('Fetching predictions', predictionOpts);

	const [, result] = await to(
		autocompleteService.getPlacePredictions(predictionOpts),
	);

	const rawResults = toArray(result?.predictions);

	return getFilteredPredictions(rawResults, options).map((suggest) => ({
		address: suggest.description,
		place_id: suggest.place_id,
		tags: getTags(suggest),
	}));
}

export function getCacheKey(location: Location): string | null {
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing --  we don't want empty strings
	return location.place_id || location.address || null;
}

const isCompassGeocodeLocation = (
	location: google.maps.GeocoderResult | CompassGeocodeLocation,
): location is CompassGeocodeLocation => {
	return !Object.hasOwn(location, 'address_components');
};

export function isAmbiguousLocality(
	location: google.maps.GeocoderResult | CompassGeocodeLocation,
) {
	if (isCompassGeocodeLocation(location)) {
		return Boolean(location.google_postcode_localities?.length);
	}

	return Boolean(location.postcode_localities?.length);
}

export function dedupeLocality(
	geocodedLocation: GeocodedLocation,
	localities: string[],
	searchedAddress?: string,
	onInferLocality?: LocationSelectProps['onInferLocality'],
): GeocodedLocation {
	const locationParts = searchedAddress?.split(',').map((part) => part.trim());

	const originalCity = geocodedLocation.city;
	const inferredCity = locationParts?.find((part) =>
		localities.find(
			(locality) => locality.toLowerCase() === part.toLowerCase(),
		),
	);

	if (inferredCity !== originalCity) {
		onInferLocality?.({
			localities,
			originalCity,
			inferredCity,
			searchedAddress,
		});

		if (inferredCity) {
			const dedupedLocation: GeocodedLocation = {
				...geocodedLocation,
				city: inferredCity,
				address: undefined,
			};

			dedupedLocation.address = getFormattedLocation(dedupedLocation);

			return dedupedLocation;
		}
	}

	return geocodedLocation;
}

export async function getLocationDetails(
	query: string,
	location: Location,
	options?: FetchLocationOptions,
	compassConfig?: CompassConfig,
	shouldInferAmbiguousLocality?: LocationSelectProps['shouldInferAmbiguousLocality'],
	onInferLocality?: LocationSelectProps['onInferLocality'],
): Promise<GeocodedLocation | null> {
	const key = getCacheKey(location);

	if (!key) {
		// this supports legacy locations, that were not saved with their `place_id` or `address`
		logger.info(
			'Skipping get location details; location does not have a `place_id` or `address` property',
			{
				location,
			},
		);

		return Promise.resolve(location);
	}

	if (!isBlank(location.state ?? '') && !isBlank(location.country ?? '')) {
		logger.info('Skipping get location details; details already present', {
			location,
		});

		return Promise.resolve(location);
	}

	if (cache.has(key)) {
		return cache.get(key);
	}

	async function fetchFromCompass(): Promise<GeocodedLocation> {
		const { baseURL, APIKey } = compassConfig ?? {};
		if (!compassConfig || !APIKey || !baseURL) {
			logger.info('Skipping Compass. Missing compassConfig');
			throw new Error('Skipping Compass. Missing baseURL or APIKey');
		}

		logger.info('Fetching address geocoding from Compass', {
			googlePlaceId: location.place_id,
		});

		try {
			const compassService = new CompassService({
				baseURL,
				APIKey,
			});
			const compassResult = await compassService.geocode({
				googlePlaceId: location.place_id,
			});

			let geocodedLocation: GeocodedLocation = {
				...location,
				...parseCompassGeocodeLocationToGeocodedLocation(compassResult),
			};

			if (shouldInferAmbiguousLocality && isAmbiguousLocality(compassResult)) {
				geocodedLocation = dedupeLocality(
					geocodedLocation,
					compassResult.google_postcode_localities!,
					location.address,
					onInferLocality,
				);
			}

			geocodedLocation = applyCustomParser(query, geocodedLocation, options);

			const cacheKey = getCacheKey(geocodedLocation);

			if (cacheKey) {
				cache.add(cacheKey, geocodedLocation);
			}

			return geocodedLocation;
		} catch (error) {
			logger.error('Failed to get geocoded location from Compass', {
				query,
				address: location.address,
				place_id: location.place_id,
			});
			return Promise.reject(
				new Error('Failed to get geocoded location from Compass'),
			);
		}
	}

	async function fetchFromGeocoderAPI(): Promise<GeocodedLocation> {
		const geocoder = getGeocoder();

		if (!geocoder) {
			logger.error('Geocoder not loaded');
			throw new Error('Geocoder not loaded');
		}

		logger.info('Fetching address geocoding', {
			query,
			address: location.address,
		});

		const geocoderOptions = {
			...(location.place_id
				? { placeId: location.place_id }
				: { address: location.address }),
		};

		try {
			/**
			 * Source: https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingRequests
			 */
			const { results } = await geocoder.geocode(geocoderOptions);
			const place = results[0];

			let geocodedLocation: GeocodedLocation = {
				...location,
				...parseResult(place),
			};

			if (shouldInferAmbiguousLocality && isAmbiguousLocality(place)) {
				geocodedLocation = dedupeLocality(
					geocodedLocation,
					place.postcode_localities!,
					location.address,
					onInferLocality,
				);
			}

			geocodedLocation = applyCustomParser(query, geocodedLocation, options);

			const cacheKey = getCacheKey(geocodedLocation);

			if (cacheKey) {
				cache.add(cacheKey, geocodedLocation);
			}

			return geocodedLocation;
		} catch {
			throw new Error('Failed to get location geocoding');
		}
	}

	function fetchFromPlacesAPI(): Promise<GeocodedLocation> {
		return new Promise((resolve, reject) => {
			const placesService = getPlacesService();

			if (!placesService) {
				logger.error('Places Service not loaded');
				reject(new Error('Places Service not loaded'));

				return;
			}

			if (!location.place_id) {
				reject(new Error('Failed to get location details. Missing `placeId`'));

				return;
			}

			logger.info('Fetching place details', {
				query,
				address: location.address,
				place_id: location.place_id,
			});

			const placesOptions = {
				placeId: location.place_id,
				fields: getFields(options),
			};

			/**
			 * Source: https://developers.google.com/maps/documentation/places/web-service/details#PlaceDetailsRequests
			 */
			placesService.getDetails(placesOptions, (results, status) => {
				if (status === getGoogleMaps()!.places.PlacesServiceStatus.OK) {
					let geocodedLocation: GeocodedLocation = {
						...location,
						...parseResult(results ?? {}),
					};

					geocodedLocation = applyCustomParser(
						query,
						geocodedLocation,
						options,
					);

					const cacheKey = getCacheKey(geocodedLocation);

					if (cacheKey) {
						cache.add(cacheKey, geocodedLocation);
					}

					resolve(geocodedLocation);
				} else {
					reject(new Error('Failed to get location details'));
				}
			});
		});
	}

	{
		/**
		 * Firstly, our initial approach involves getting data from Compass
		 * an internal tool developed by Loadsmart that offers a cache implementation
		 * which is the best solution in terms of costs.
		 */
		const [error, result] = await to(fetchFromCompass());

		if (!error) {
			return result ?? null;
		}
	}

	{
		/**
		 * If running Compass fails, we try geocoding, as this is less expensive than using Place API.
		 */
		const [error, result] = await to(fetchFromGeocoderAPI());

		if (!error) {
			return result ?? null;
		}
	}

	logger.error('Failed fetching address geocoding', {
		query,
		address: location.address,
		place_id: location.place_id,
	});

	{
		/**
		 * If running Geocoding fails, we try Places API.
		 */
		const [error, result] = await to(fetchFromPlacesAPI());

		if (!error) {
			return result ?? null;
		}

		throw error;
	}
}

/**
 * Billing data source: https://developers.google.com/maps/documentation/places/web-service/usage-and-billing
 * Pricing calculation as of Apr, 08, 2022.
 * Best case scenario: Autocomplete per request + Geocoding = USD 0.00783 (no session token)
 * Worst case scenario: Autocomplete per request + Place details = USD 0.01983  (no session token)
 *
 * Updated on May, 23, 2023.
 * The best case scenario now is:
 *   Autocomplete per request + Compass = From USD 0 (if the location is cached) to USD 0.00783 (no session token)
 */
export function generateGoogleMapsDatasource(options: FetchLocationOptions) {
	return function GoogleMapsDatasource(): DataSource<Location[]> {
		const datasource: DataSource<Location[]> = {
			type: DATASOURCE_TYPE,
			adapter: getAdapter(options),
			async fetch({ query }) {
				if (String(query || '').length < 3) {
					return [];
				}

				const suggestions = await getPlacePredictions(query, options);

				return suggestions;
			},
		};

		return datasource;
	};
}
